// ===================== Procedural WebAudio — SFX + layered dynamic music =====================
// No asset files: every sound is synthesized. The music is a 64-step sequencer
// with independent layers (pad / bass / kick / hats / snare / lead) that fade
// in as the run's intensity rises, plus a "boss mode" tension layer.
class AudioEngine {
  constructor() {
    this.ctx = null;
    this.master = null;     // SFX bus
    this.musicGain = null;  // music bus
    this.lowpass = null;    // shared output filter (pause muffle)
    this.muted = false;
    this.volume = 0.6;      // 0..1 master
    this.enabled = true;
    this._lastTime = {};
    // music state
    this._musicTimer = null;
    this._musicStep = 0;
    this._musicKey = null;
    this._lastMusicKey = null;    // survives stopMusic(), so music can be resumed
    this._musicEnabled = true;
    this._intensity = 0.3;        // smoothed 0..1 — drives which layers play
    this._targetIntensity = 0.3;
    this._bossMode = false;
    this._paused = false;
    // pickup pitch streak
    this._streak = 0;
    this._streakAt = 0;
    this._noiseBuf = null;
    // Deferred init: browsers block AudioContexts created before a user
    // gesture (and log a console warning per blocked attempt). We create
    // nothing until `_unlock()` runs from a genuine gesture; until then every
    // public entry point is a silent no-op (`this.ctx` is null).
    this._unlocked = false;
    this._pendingMusicKey = null;   // music requested before unlock — replayed after
    this._unlockHandler = null;
    this._armUnlock();
  }
  // Once-only gesture listeners (guarded so this module still imports cleanly
  // in plain Node for offline balance models). They disarm after the first
  // successful unlock; a non-activating key (e.g. Escape) leaves them armed.
  _armUnlock() {
    if (typeof window === 'undefined' || !window.addEventListener) return;
    this._unlockHandler = () => this._unlock();
    window.addEventListener('pointerdown', this._unlockHandler, { capture: true, passive: true });
    window.addEventListener('keydown', this._unlockHandler, { capture: true });
  }
  _disarmUnlock() {
    if (!this._unlockHandler) return;
    window.removeEventListener('pointerdown', this._unlockHandler, { capture: true });
    window.removeEventListener('keydown', this._unlockHandler, { capture: true });
    this._unlockHandler = null;
  }
  _unlock() {
    if (this._unlocked) {
      // Already initialised — just recover from a browser-side suspend.
      if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume();
      return;
    }
    // Not every event grants activation (e.g. Escape keydown). Creating the
    // context anyway would just re-trigger the autoplay warnings; wait for a
    // gesture that counts. (Browsers without the API behave as before.)
    if (typeof navigator !== 'undefined' && navigator.userActivation &&
        !navigator.userActivation.hasBeenActive) return;
    if (!this._ensure()) { this._disarmUnlock(); return; }   // WebAudio unavailable — permanent no-op
    this._unlocked = true;
    this._disarmUnlock();
    if (this.ctx.state === 'suspended') this.ctx.resume();
    // Music asked for before audio was allowed (title theme) starts now.
    if (this._pendingMusicKey) {
      const key = this._pendingMusicKey;
      this._pendingMusicKey = null;
      this.startMusic(key);
    }
  }
  _ensure() {
    if (this.ctx) return true;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) { this.enabled = false; return false; }
      this.ctx = new AC();
      this.lowpass = this.ctx.createBiquadFilter();
      this.lowpass.type = 'lowpass';
      // Honour a pause that happened before deferred init.
      this.lowpass.frequency.value = this._paused ? 420 : 18000;
      this.lowpass.connect(this.ctx.destination);
      this.master = this.ctx.createGain();
      this.musicGain = this.ctx.createGain();
      this._applyGains();
      this.master.connect(this.lowpass);
      this.musicGain.connect(this.lowpass);
      return true;
    } catch { this.enabled = false; return false; }
  }
  _applyGains() {
    if (this.master) this.master.gain.value = this.muted ? 0 : this.volume * 0.6;
    if (this.musicGain) this.musicGain.gain.value = this.muted ? 0 : this.volume * 0.17;
  }
  // Public unlock hook — callers invoke this from their own gesture handlers.
  resume() { this._unlock(); }
  // Hard-stop all output while the app is backgrounded/minimised — the pause
  // muffle only filters; a hidden WebView keeps rendering WebAudio otherwise.
  suspend() {
    if (this.ctx && this.ctx.state === 'running') this.ctx.suspend().catch(() => {});
  }
  setVolume(v) { this.volume = Math.max(0, Math.min(1, v)); this._applyGains(); }
  setMuted(b) { this.muted = !!b; this._applyGains(); return this.muted; }
  toggleMute() { return this.setMuted(!this.muted); }

  // Muffle everything behind a low-pass while paused (AAA pause feel).
  setPaused(b) {
    b = !!b;
    if (b === this._paused) return;
    this._paused = b;
    if (!this.ctx || !this.lowpass) return;
    const t = this.ctx.currentTime;
    this.lowpass.frequency.cancelScheduledValues(t);
    this.lowpass.frequency.setTargetAtTime(b ? 420 : 18000, t, 0.08);
  }

  // Game feeds 0..1 each frame; layers fade in/out as it moves.
  setIntensity(v) { this._targetIntensity = Math.max(0, Math.min(1, v)); }
  setBossMode(b) { this._bossMode = !!b; }

  // Rate-limit a given sound so bursts don't stack into a wall of noise.
  _throttle(key, minGap) {
    if (!this.ctx) return false;    // pre-gesture: no context yet, drop the sound
    const now = this.ctx.currentTime;
    if (this._lastTime[key] && now - this._lastTime[key] < minGap) return false;
    this._lastTime[key] = now;
    return true;
  }

  _tone({ freq = 440, type = 'square', dur = 0.1, vol = 0.3, sweep = 0, delay = 0, vary = 0.04 }) {
    if (!this.enabled || this.muted || !this.ctx) return;   // silent until first gesture
    if (vary) freq *= 1 + (Math.random() * 2 - 1) * vary;   // humanize — no two hits identical
    const t0 = this.ctx.currentTime + delay;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (sweep) osc.frequency.exponentialRampToValueAtTime(Math.max(20, freq + sweep), t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(vol, t0 + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g); g.connect(this.master);
    osc.start(t0); osc.stop(t0 + dur + 0.02);
  }

  // Shared white-noise buffer for percussion / impacts.
  _noise() {
    if (!this._noiseBuf) {
      const len = Math.floor(this.ctx.sampleRate * 0.5);
      this._noiseBuf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const d = this._noiseBuf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    }
    return this._noiseBuf;
  }
  _noiseHit({ dur = 0.08, vol = 0.2, hp = 4000, delay = 0, bus = null, at = null }) {
    if (!this.enabled || this.muted || !this.ctx) return;   // silent until first gesture
    const t0 = (at !== null ? at : this.ctx.currentTime) + delay;
    const src = this.ctx.createBufferSource();
    src.buffer = this._noise();
    const f = this.ctx.createBiquadFilter();
    f.type = 'highpass'; f.frequency.value = hp;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(vol, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(f); f.connect(g); g.connect(bus || this.master);
    src.start(t0); src.stop(t0 + dur + 0.02);
  }

  // Briefly dip the music so a big SFX moment cuts through.
  _duck(amount = 0.4, time = 0.5) {
    if (!this.ctx || !this.musicGain || this.muted) return;
    const base = this.volume * 0.17;
    const t = this.ctx.currentTime;
    this.musicGain.gain.cancelScheduledValues(t);
    this.musicGain.gain.setValueAtTime(base * (1 - amount), t);
    this.musicGain.gain.setTargetAtTime(base, t + 0.05, time);
  }

  // ------------------------------------------------------------------- SFX
  shoot(el) {
    if (!this._throttle('shoot', 0.045)) return;
    const map = {
      fire: 300, ice: 620, lightning: 900, poison: 240, wind: 720, holy: 820, neutral: 480,
      // Ascendant forms sit ±60 Hz off their base element's voice.
      inferno: 360, absolutezero: 560, ioncascade: 960, blight: 180, maelstrom: 780, zenith: 880,
    };
    this._tone({ freq: map[el] || 480, type: 'square', dur: 0.07, vol: 0.12, sweep: -180 });
  }
  hit() {
    if (!this._throttle('hit', 0.03)) return;
    this._tone({ freq: 180, type: 'triangle', dur: 0.05, vol: 0.10, sweep: -60 });
  }
  // XP pickups climb in pitch while you keep vacuuming — resets after a beat.
  pickup() {
    if (!this.enabled || !this.ctx) return;   // silent until first gesture
    const now = this.ctx.currentTime;
    if (now - this._streakAt > 0.9) this._streak = 0;
    this._streakAt = now;
    this._streak = Math.min(this._streak + 1, 24);
    if (!this._throttle('pickup', 0.03)) return;
    const freq = 660 * Math.pow(1.05946, Math.min(this._streak, 16));
    this._tone({ freq, type: 'sine', dur: 0.07, vol: 0.14, sweep: 200, vary: 0 });
  }
  gold() { this._tone({ freq: 1040, type: 'square', dur: 0.06, vol: 0.12, sweep: 200 }); }
  hurt() {
    this._tone({ freq: 220, type: 'sawtooth', dur: 0.18, vol: 0.28, sweep: -140 });
    this._noiseHit({ dur: 0.12, vol: 0.14, hp: 900 });
    this._duck(0.35, 0.3);
  }
  levelup() {
    this._tone({ freq: 523, type: 'square', dur: 0.1, vol: 0.2, vary: 0 });
    this._tone({ freq: 659, type: 'square', dur: 0.1, vol: 0.2, delay: 0.08, vary: 0 });
    this._tone({ freq: 784, type: 'square', dur: 0.16, vol: 0.22, delay: 0.16, vary: 0 });
    this._duck(0.3, 0.4);
  }
  multiply() {
    this._tone({ freq: 660, type: 'square', dur: 0.09, vol: 0.22, sweep: 400, vary: 0 });
    this._tone({ freq: 990, type: 'square', dur: 0.12, vol: 0.2, sweep: 500, delay: 0.06, vary: 0 });
  }
  boss() {
    this._tone({ freq: 90, type: 'sawtooth', dur: 0.6, vol: 0.34, sweep: 40 });
    this._tone({ freq: 130, type: 'square', dur: 0.5, vol: 0.22, delay: 0.1 });
    this._noiseHit({ dur: 0.5, vol: 0.2, hp: 300 });
    this._duck(0.6, 0.8);
  }
  death() {
    this._tone({ freq: 400, type: 'sawtooth', dur: 0.5, vol: 0.3, sweep: -340 });
  }
  chest() {
    this._tone({ freq: 700, type: 'square', dur: 0.1, vol: 0.2, sweep: 200, vary: 0 });
    this._tone({ freq: 1000, type: 'sine', dur: 0.2, vol: 0.2, sweep: 300, delay: 0.09, vary: 0 });
  }
  ui() { this._tone({ freq: 600, type: 'square', dur: 0.04, vol: 0.08, vary: 0 }); }

  // ---------------- layered procedural music ----------------
  // `at` is an absolute AudioContext time. The sequencer always passes one so
  // note timing is bound to the audio clock, never to when the JS timer fired.
  _note(freq, type, dur, vol, delay = 0, at = null) {
    if (!this.ctx || !this.musicGain) return;
    const t0 = (at !== null ? at : this.ctx.currentTime + 0.02) + delay;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(vol, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g); g.connect(this.musicGain);
    osc.start(t0); osc.stop(t0 + dur + 0.03);
  }
  _kick(vol, at = null) {
    if (!this.ctx || !this.musicGain) return;
    const t0 = at !== null ? at : this.ctx.currentTime + 0.02;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(130, t0);
    osc.frequency.exponentialRampToValueAtTime(38, t0 + 0.12);
    g.gain.setValueAtTime(vol, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
    osc.connect(g); g.connect(this.musicGain);
    osc.start(t0); osc.stop(t0 + 0.2);
  }

  // Callers cache the track they asked for and won't re-ask (see startMusic),
  // so switching music back on has to restart the last track itself — otherwise
  // the toggle reads ON and stays silent until the key changes, which mid-run
  // means the rest of the run.
  setMusicEnabled(on) {
    this._musicEnabled = on;
    if (!on) {
      this._lastMusicKey = this._musicKey || this._pendingMusicKey || this._lastMusicKey;
      this.stopMusic();
    } else if (this._lastMusicKey) {
      this.startMusic(this._lastMusicKey);
    }
  }

  startMusic(key) {
    if (!this.enabled || !this._musicEnabled) return;
    // Before the first gesture there is no context — remember what was asked
    // for and start it from `_unlock()` (callers cache the key and won't re-ask).
    if (!this.ctx) { this._pendingMusicKey = key; return; }
    if (this._musicKey === key && this._musicTimer) return;
    this.stopMusic();
    const mood = MOODS[key] || MOODS.menu;
    this._musicKey = key;
    this._musicStep = 0;
    this._stepDur = (mood.stepMs || 190) / 1000;
    this._nextStepTime = this.ctx.currentTime + 0.06;
    const pump = () => this._musicPump(mood);
    pump();
    this._musicTimer = setInterval(pump, MUSIC_TIMER_MS);
  }

  // Look-ahead scheduler. The JS timer only decides WHEN WE SCHEDULE; every
  // note is pinned to an exact AudioContext time, so main-thread jitter can no
  // longer move the beat. Firing one step per setInterval tick (the previous
  // approach) measured +/-25% wobble on a 16th-note grid, worst exactly when
  // the game was busiest.
  _musicPump(mood) {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    // Backgrounded tab or a long stall: don't try to catch up a huge backlog.
    if (this._nextStepTime < now) this._nextStepTime = now + 0.02;
    while (this._nextStepTime < now + MUSIC_LOOKAHEAD) {
      this._musicTick(mood, this._nextStepTime);
      this._nextStepTime += this._stepDur;
    }
  }

  // How strongly a layer plays for the current intensity (0 below its
  // threshold, ramping to 1 as intensity climbs past it).
  _layer(th) {
    const i = this._intensity;
    if (i <= th) return 0;
    return Math.min(1, (i - th) / Math.max(0.18, 1 - th));
  }

  _musicTick(mood, at) {
    // Smooth intensity toward the game's target so layers fade, not snap.
    this._intensity += (this._targetIntensity - this._intensity) * 0.06;
    const s = this._musicStep;
    const semi = (root, n) => root * Math.pow(2, n / 12);
    const boss = this._bossMode;

    // PAD — always on: a slow chord swell every bar anchors the key.
    if (s % 16 === 0) {
      const padVol = 0.16 + this._layer(0) * 0.05;
      this._note(semi(mood.root, mood.bass[0]), 'sine', 3.4, padVol, 0, at);
      this._note(semi(mood.root, mood.bass[0] + (mood.minor ? 3 : 4)), 'sine', 3.4, padVol * 0.7, 0, at);
      this._note(semi(mood.root, mood.bass[0] + 7), 'sine', 3.4, padVol * 0.55, 0, at);
    }
    // BASS — on the beat.
    const bassAmt = this._layer(0.08);
    if (bassAmt > 0 && s % 4 === 0) {
      const bassN = mood.bass[(s / 4) % mood.bass.length];
      this._note(semi(mood.root, bassN), mood.type, 0.34, 0.55 * bassAmt, 0, at);
      if (boss) this._note(semi(mood.root, bassN + 1), 'sawtooth', 0.2, 0.22 * bassAmt, 0, at); // tension semitone
    }
    // KICK — four on the floor as pressure builds.
    const kickAmt = this._layer(0.3);
    if (kickAmt > 0 && s % 4 === 0) this._kick(0.8 * kickAmt, at);
    // HATS — offbeats; double-time in boss mode.
    const hatAmt = this._layer(0.5);
    if (hatAmt > 0 && (boss ? s % 2 === 0 : s % 4 === 2)) {
      this._noiseHit({ dur: 0.04, vol: 0.05 * hatAmt, hp: 8000, bus: this.musicGain, at });
    }
    // SNARE — backbeat at high intensity.
    const snareAmt = this._layer(0.68);
    if (snareAmt > 0 && s % 16 === 8) {
      this._noiseHit({ dur: 0.11, vol: 0.11 * snareAmt, hp: 1800, bus: this.musicGain, at });
    }
    // LEAD ARP — the melody; jumps an octave in the loop's back half when hot.
    const leadAmt = this._layer(0.18);
    if (leadAmt > 0 && s % 2 === 0) {
      const leadN = mood.seq[(s / 2) % mood.seq.length];
      const oct = (this._intensity > 0.75 && s >= 32) ? 4 : 2;
      this._note(semi(mood.root * oct, leadN), mood.lead, 0.18, 0.45 * leadAmt, 0, at);
    }
    // Shimmer — an airy high echo, occasionally.
    if (s % 16 === 12) {
      const leadN = mood.seq[(s / 2) % mood.seq.length];
      this._note(semi(mood.root * 4, leadN), 'sine', 0.4, 0.12 + 0.08 * this._layer(0.4), 0, at);
    }
    this._musicStep = (s + 1) % 64;
  }

  stopMusic() {
    if (this._musicTimer) { clearInterval(this._musicTimer); this._musicTimer = null; }
    this._musicKey = null;
    this._pendingMusicKey = null;
  }
}

// Look-ahead sequencing: check often, schedule a little way ahead.
const MUSIC_LOOKAHEAD = 0.12;   // seconds of notes queued on the audio clock
const MUSIC_TIMER_MS = 25;      // how often we top that queue up

// Per-biome musical moods (root freq + semitone offsets, 16th-note step time).
const MOODS = {
  menu:   { root: 196, seq: [0, 7, 10, 12, 10, 7, 5, 3], bass: [0, 0, 5, 7], type: 'sine', lead: 'triangle', minor: true, stepMs: 210 },
  meadow: { root: 196, seq: [0, 7, 12, 7, 3, 10, 7, 3], bass: [0, 0, -5, -5], type: 'triangle', lead: 'square', minor: true, stepMs: 190 },
  cavern: { root: 174, seq: [0, 3, 7, 10, 7, 3, 0, -2], bass: [0, 0, 3, 3], type: 'sine', lead: 'triangle', minor: true, stepMs: 200 },
  wastes: { root: 220, seq: [0, 5, 8, 12, 8, 5, 3, 0], bass: [0, -3, -5, -3], type: 'sawtooth', lead: 'square', minor: true, stepMs: 175 },
  void:   { root: 146, seq: [0, 6, 7, 12, 10, 6, 3, 0], bass: [0, 0, -2, -4], type: 'sine', lead: 'triangle', minor: true, stepMs: 205 },
};

export const Audio = new AudioEngine();
